import dayjs, { type Dayjs } from "dayjs";

export type DayAbsenceRow = {
  timeFrom: string;
  timeTo: string;
  absenceLastDate: string | null;
};

export type DayAbsenceShift = { start: string; end: string };

export type DayAbsence<T extends DayAbsenceRow> = {
  kind: "whole" | "part";
  rows: [T, ...T[]];
};

export type DayAbsenceDescription =
  | { kind: "until"; date: string }
  | { kind: "allDay" }
  | { kind: "from"; time: string }
  | { kind: "to"; time: string }
  | { kind: "between"; from: string; to: string }
  | { kind: "partDay" };

// The end is exclusive, so a span ending at next-day 00:00 stays on its own day.
export function spansDate(start: string, end: string, date: string): boolean {
  const dayStart = dayjs(date).startOf("day");
  return (
    dayjs(start).isBefore(dayStart.add(1, "day")) &&
    dayjs(end).isAfter(dayStart)
  );
}

export function resolveDayAbsence<T extends DayAbsenceRow>(
  rows: T[],
  shifts: DayAbsenceShift[],
  date: string,
): DayAbsence<T> | null {
  const [first, ...rest] = rows.filter((row) =>
    spansDate(row.timeFrom, row.timeTo, date),
  );
  if (first == null) return null;
  const dayRows: [T, ...T[]] = [first, ...rest];

  const rowSpans = dayRows.map((row) =>
    withinDate(row.timeFrom, row.timeTo, date),
  );
  function covers(span: DaySpan) {
    return rowSpans.some(
      (row) => !row.start.isAfter(span.start) && !row.end.isBefore(span.end),
    );
  }

  const dayShifts = shifts.filter((shift) =>
    spansDate(shift.start, shift.end, date),
  );
  const covered =
    dayShifts.length === 0
      ? covers(wholeDate(date))
      : dayShifts.every((shift) =>
          covers(withinDate(shift.start, shift.end, date)),
        );
  return { kind: covered ? "whole" : "part", rows: dayRows };
}

export function describeDayAbsence<T extends DayAbsenceRow>(
  absence: DayAbsence<T>,
  shifts: DayAbsenceShift[],
  date: string,
): DayAbsenceDescription {
  if (absence.kind === "whole") {
    const lastDate = absence.rows.reduce<string | null>(
      (latest, row) =>
        row.absenceLastDate != null &&
        (latest == null || row.absenceLastDate > latest)
          ? row.absenceLastDate
          : latest,
      null,
    );
    return lastDate != null && lastDate > date
      ? { kind: "until", date: lastDate }
      : { kind: "allDay" };
  }

  const dayShifts = shifts
    .filter((shift) => spansDate(shift.start, shift.end, date))
    .map((shift) => withinDate(shift.start, shift.end, date));
  const rowSpans = absence.rows.map((row) =>
    withinDate(row.timeFrom, row.timeTo, date),
  );
  const absenceStart = earliest(rowSpans.map((span) => span.start));
  const absenceEnd = latest(rowSpans.map((span) => span.end));
  const shiftStart = earliest(dayShifts.map((span) => span.start));
  const shiftEnd = latest(dayShifts.map((span) => span.end));

  if (dayShifts.length === 0 && absenceStart != null && absenceEnd != null) {
    return {
      kind: "between",
      from: absenceStart.format("HH:mm"),
      to: absenceEnd.format("HH:mm"),
    };
  }

  const startsLate =
    absenceStart != null &&
    shiftStart != null &&
    absenceStart.isAfter(shiftStart);
  const endsEarly =
    absenceEnd != null && shiftEnd != null && absenceEnd.isBefore(shiftEnd);

  if (startsLate && endsEarly) {
    return {
      kind: "between",
      from: absenceStart.format("HH:mm"),
      to: absenceEnd.format("HH:mm"),
    };
  }
  if (startsLate) return { kind: "from", time: absenceStart.format("HH:mm") };
  if (endsEarly) return { kind: "to", time: absenceEnd.format("HH:mm") };
  return { kind: "partDay" };
}

type DaySpan = { start: Dayjs; end: Dayjs };

// Clipped to the date's midnights, as the server reads an absence's edges, so an overnight shift counts only its part on the date.
function withinDate(start: string, end: string, date: string): DaySpan {
  const dayStart = dayjs(date).startOf("day");
  const dayEnd = dayStart.add(1, "day");
  const from = dayjs(start);
  const to = dayjs(end);
  return {
    start: from.isBefore(dayStart) ? dayStart : from,
    end: to.isAfter(dayEnd) ? dayEnd : to,
  };
}

// Ends a minute early: the old writer stored a full day as 00:00–23:59:59.
function wholeDate(date: string): DaySpan {
  const dayStart = dayjs(date).startOf("day");
  return { start: dayStart, end: dayStart.add(1, "day").subtract(1, "minute") };
}

function earliest(times: Dayjs[]): Dayjs | null {
  return times.reduce<Dayjs | null>(
    (min, time) => (min == null || time.isBefore(min) ? time : min),
    null,
  );
}

function latest(times: Dayjs[]): Dayjs | null {
  return times.reduce<Dayjs | null>(
    (max, time) => (max == null || time.isAfter(max) ? time : max),
    null,
  );
}
